import { create } from 'zustand'
import { supabase } from '@/lib/supabase'
import type { Achievement, UserAchievement, AchievementProgress } from '@/types/achievements'

interface AchievementState {
  achievements: Achievement[]
  userAchievements: UserAchievement[]
  achievementProgress: AchievementProgress[]
  loading: boolean
  error: string | null
  recentUnlocks: Achievement[]

  fetchAchievements: () => Promise<void>
  fetchUserAchievements: (userId: string) => Promise<void>
  fetchAchievementProgress: (userId: string) => Promise<void>
  checkAndAwardAchievement: (userId: string, achievementName: string) => Promise<boolean>
  updateAchievementProgress: (userId: string, achievementId: string, progress: number) => Promise<void>
  clearRecentUnlocks: () => void
}

export const useAchievementStore = create<AchievementState>((set, get) => ({
  achievements: [],
  userAchievements: [],
  achievementProgress: [],
  loading: false,
  error: null,
  recentUnlocks: [],

  fetchAchievements: async () => {
    set({ loading: true, error: null })
    try {
      const { data, error } = await supabase
        .from('achievements')
        .select('*')
        .order('tier', { ascending: true })
        .order('category', { ascending: true })

      if (error) throw error
      set({ achievements: data || [], loading: false })
    } catch (error) {
      set({ error: (error as Error).message, loading: false })
    }
  },

  fetchUserAchievements: async (userId: string) => {
    set({ loading: true, error: null })
    try {
      const { data, error } = await supabase
        .from('user_achievements')
        .select('*, achievement:achievements(*)')
        .eq('user_id', userId)
        .order('unlocked_at', { ascending: false })

      if (error) throw error
      set({ userAchievements: data || [], loading: false })
    } catch (error) {
      set({ error: (error as Error).message, loading: false })
    }
  },

  fetchAchievementProgress: async (userId: string) => {
    set({ loading: true, error: null })
    try {
      const { data, error } = await supabase
        // @ts-ignore - Supabase RPC type inference issue
        .rpc('get_achievement_progress', { p_user_id: userId })

      if (error) throw error
      set({ achievementProgress: data || [], loading: false })
    } catch (error) {
      set({ error: (error as Error).message, loading: false })
    }
  },

  checkAndAwardAchievement: async (userId: string, achievementName: string) => {
    try {
      const { data, error } = await supabase
        // @ts-ignore - Supabase RPC type inference issue
        .rpc('check_and_award_achievement', {
          p_user_id: userId,
          p_achievement_name: achievementName,
        })

      if (error) throw error

      // If achievement was awarded (returned true)
      if (data) {
        // Fetch the achievement details for the unlock animation
        const achievement = get().achievements.find((a) => a.name === achievementName)
        if (achievement) {
          set((state) => ({
            recentUnlocks: [...state.recentUnlocks, achievement],
          }))
        }

        // Refresh user achievements
        await get().fetchUserAchievements(userId)
        await get().fetchAchievementProgress(userId)
      }

      return data || false
    } catch (error) {
      console.error('Error checking/awarding achievement:', error)
      return false
    }
  },

  updateAchievementProgress: async (userId: string, achievementId: string, progress: number) => {
    try {
      // Update or insert progress
      const { error } = await supabase
        .from('user_achievements')
        // @ts-ignore - Supabase type inference issue with Upsert types
        .upsert([{
          user_id: userId,
          achievement_id: achievementId,
          progress,
        }], {
          onConflict: 'user_id,achievement_id',
          ignoreDuplicates: false,
        })

      if (error) throw error

      // Refresh achievement progress
      await get().fetchAchievementProgress(userId)
    } catch (error) {
      console.error('Error updating achievement progress:', error)
    }
  },

  clearRecentUnlocks: () => {
    set({ recentUnlocks: [] })
  },
}))
